import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';

export interface Values {
  // biome-ignore lint/suspicious/noExplicitAny: needed here
  [key: string]: any;
}

function isValuesCollection(input: unknown): input is Values {
  return isObject(input) && !Array.isArray(input) && !(input instanceof Date);
}

const EMPTY_VALUES: unknown[] = [undefined, null, ''];

// if newValue is empty string, null, undefined -> Return standardized empty null value
function normalizedValue(newValue: unknown): unknown {
  if (EMPTY_VALUES.includes(newValue)) {
    return null;
  }

  return newValue;
}

// Function compares two objects and return changed values.
// Also changed value is normalized
export function difference(newValues: Values, oldValues: Values): Values {
  return Object.entries(newValues).reduce((accumulated: Values, [key, newValue]: [string, unknown]) => {
    const oldValue = oldValues[key];
    const hasValueChanged = !isEqual(newValue, oldValue);

    if (!hasValueChanged) {
      return accumulated;
    }

    accumulated[key] =
      isValuesCollection(newValue) && isValuesCollection(oldValue)
        ? difference(newValue, oldValue)
        : normalizedValue(newValue);

    return accumulated;
  }, {});
}

/** Deeply compare values, if old value is null and new value is among EMPTY_VALUES, keep old null value */
export const deepNormalizeValues = <Values extends object = Record<string, unknown>>(
  newValues: Values,
  oldValues: Values,
  emptyValues = EMPTY_VALUES
): Values => {
  const acc = {} as Values;

  for (const key of Object.keys(newValues)) {
    const newValue = newValues[key];
    const oldValue = oldValues[key];

    if (isValuesCollection(newValue) && isValuesCollection(oldValue)) {
      acc[key] = deepNormalizeValues(newValue, oldValue);
    } else {
      acc[key] = oldValue === null && emptyValues.includes(newValue) ? null : newValue;
    }
  }

  return acc;
};

export const differenceByAllKeys = <Values extends object = Record<string, unknown>>(
  newValues: Values,
  oldValues: Values
): Values => {
  const keys = new Set([...Object.keys(newValues), ...Object.keys(oldValues)]);
  const acc = {} as Values;

  for (const key of keys) {
    const newValue = newValues[key];
    const oldValue = oldValues[key];

    /** This covers case where OLD value is null, OLD !== NEW, and the NEW value would be converted to null upon "normalization" */
    /** This prevents including key in "diff" when value didn't actually change */
    if (oldValue === null && EMPTY_VALUES.includes(newValue)) {
      continue;
    }

    const hasValueChanged = !isEqual(newValue, oldValue);

    if (!hasValueChanged) {
      continue;
    }

    acc[key] =
      isValuesCollection(newValue) && isValuesCollection(oldValue)
        ? differenceByAllKeys(newValue, oldValue)
        : normalizedValue(newValue);
  }

  return acc;
};
